import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const epcrTreatmentMedicationSlice = createSlice({
    name: 'epcrTreatmentMedications',
    initialState: {
        data: {},
        defs: []
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const med of ensureArray(action.payload)) {
                if (med.dose) {
                    med.dose = parseFloat(med.dose);
                }
                if (!state.data[med.pcrid]) {
                    state.data[med.pcrid] = [med];
                } else {
                    let idx = state.data[med.pcrid].findIndex(a => med.id === a.id);
                    if (idx >= 0) {
                        state.data[med.pcrid].splice(idx, 1, med);
                    } else {
                        state.data[med.pcrid] = [...state.data[med.pcrid], med];
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const med of ensureArray(action.payload)) {
                let idx = state.data[med.pcrid].findIndex(a => a.id === med.id);
                if (idx >= 0) {
                    state.data[med.pcrid].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrTreatmentMedicationSlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrTreatmentMedicationSlice.name);
const fetchEpcrTreatmentMedications = pcrid => network => network.get('/api/v1/epcrtreatmentmedications/' + encodeURIComponent(pcrid), epcrTreatmentMedicationSlice.actions.addOrUpdate);
const fetchEpcrTreatmentMedicationDefs = network => network.get('/api/v2/treatmentmedications', epcrTreatmentMedicationSlice.actions.setDefs);
export const addEpcrTreatmentMedication = (network, data) => network.put('/api/v1/epcrtreatmentmedication', data, epcrTreatmentMedicationSlice.actions.addOrUpdate, eagerMeta(data.id, { fk: data.pcrid})); // TODO better failure
export const removeEpcrTreatmentMedicaiton = (network, data) => network.delete('/api/v1/epcrtreatmentmedication/' + encodeURIComponent(data.id), epcrTreatmentMedicationSlice.actions.remove, eagerMeta(data.id, { fk: data.pcrid})); // TODO better failure
export const searchEpcrTreatmentMedications = (network, query) => network.post('/app/v2/epcrTreatmentMedicationSearch', query);
// #endregion

// #region selectors
const selectEpcrTreatmentMedicationDefs = state => state.epcrTreatmentMedications.defs;

const eagerSelector = createEagerSelector(epcrTreatmentMedicationSlice.name, { mode: 'mapToArray' });
const selectAllEpcrTreatmentMedications = state => state.epcrTreatmentMedications.data;

const selectEpcrTreatmentMedicationsByID = eagerSelector(createSelector([selectAllEpcrTreatmentMedications, (meds, pcrid) => pcrid], (meds, pcrid) => meds[pcrid] ?? []));
const selectEpcrTreatmentMedications = pcrid  => state => selectEpcrTreatmentMedicationsByID(state, pcrid);
// #endregion

// #region listeners
const listen = {
    epcrTreatmentMedicationAdded: (medication, { dispatch }) => dispatch(epcrTreatmentMedicationSlice.actions.addOrUpdate(medication)),
    epcrTreatmentMedicationRemoved: (medication, { dispatch }) => dispatch(epcrTreatmentMedicationSlice.actions.remove(medication))
}
// #endregion

export const [useEpcrTreatmentMedications, realizeEpcrTreatmentMedications] = dataHookFactory(selectEpcrTreatmentMedications, initMapFactory(fetchEpcrTreatmentMedications, listen, pcrid => pcrid?.toString()));
export const [useEpcrTreatmentMedicationDefs, realizeEpcrTreatmentMedicationDefs] = dataHookFactory(selectEpcrTreatmentMedicationDefs, { run: fetchEpcrTreatmentMedicationDefs, immortal: true });

const reducerDetails = { name: epcrTreatmentMedicationSlice.name, reducer: epcrTreatmentMedicationSlice.reducer };
export default reducerDetails;

